/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/

import React from 'react';
import { CityStats, Milestone, Grid, BuildingType } from '../../../types';
import { BUILDINGS } from '../../../constants';
import { playUiSfx } from '../../../utils/uiAudio';
import { generateAuditReport } from '../../../utils/exportUtils';

interface ChecklistTabProps {
  stats: CityStats;
  milestones: Milestone[];
  grid: Grid;
  onExportAudit: () => void;
}

const ChecklistTab: React.FC<ChecklistTabProps> = ({ stats, milestones, grid, onExportAudit }) => {
  const counts: Record<string, number> = {};
  grid.flat().forEach(tile => {
    if (tile.buildingType !== BuildingType.None && tile.isConnected) {
      counts[tile.buildingType] = (counts[tile.buildingType] || 0) + 1;
    }
  });

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-6 border-b border-white/10 pb-4">
        <h3 className="text-xl font-bold text-white uppercase tracking-wider">Operational Objectives</h3>
        <button 
          onClick={() => { playUiSfx('click'); onExportAudit(); }}
          className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded text-xs font-bold uppercase tracking-widest shadow-lg flex items-center gap-2"
        >
          <span>📥</span> Export Audit Report
        </button>
      </div>

      <div className="grid gap-4">
        {/* Core Infrastructure */}
        <div className="bg-slate-900/30 border border-white/10 rounded-lg p-6">
          <h4 className="text-lg font-bold text-cyan-400 mb-4">🏛️ Core Infrastructure</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(BUILDINGS)
              .filter(([_, building]) => (building as any).category === 'Governance')
              .map(([type, building]) => (
                <div key={type} className="flex justify-between items-center p-3 bg-white/5 rounded border border-white/10">
                  <div>
                    <div className="text-white font-medium">{(building as any).name}</div>
                    <div className="text-xs text-slate-400">{(building as any).description}</div>
                  </div>
                  <div className="text-xl font-bold text-cyan-400">
                    {counts[type] || 0}
                  </div>
                </div>
              ))}
          </div>
        </div>

        {/* Security Systems */}
        <div className="bg-slate-900/30 border border-white/10 rounded-lg p-6">
          <h4 className="text-lg font-bold text-emerald-400 mb-4">🛡️ Security Systems</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(BUILDINGS)
              .filter(([_, building]) => (building as any).category === 'Security')
              .map(([type, building]) => (
                <div key={type} className="flex justify-between items-center p-3 bg-white/5 rounded border border-white/10">
                  <div>
                    <div className="text-white font-medium">{(building as any).name}</div>
                    <div className="text-xs text-slate-400">{(building as any).description}</div>
                  </div>
                  <div className="text-xl font-bold text-emerald-400">
                    {counts[type] || 0}
                  </div>
                </div>
              ))}
          </div>
        </div>

        {/* Milestones Progress */}
        <div className="bg-slate-900/30 border border-white/10 rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h4 className="text-lg font-bold text-purple-400">🎯 Milestone Progress</h4>
            <div className="text-sm text-slate-400">
              {milestones.filter(m => m.completed).length} / {milestones.length} Complete
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="w-full bg-slate-700 rounded-full h-2 mb-6">
            <div 
              className="bg-gradient-to-r from-purple-600 to-emerald-500 h-2 rounded-full transition-all duration-500"
              style={{ width: `${(milestones.filter(m => m.completed).length / milestones.length) * 100}%` }}
            />
          </div>

          <div className="space-y-3">
            {milestones.map((milestone, idx) => {
              // Calculate progress for each milestone type
              let currentValue = 0;
              let progressPercent = 0;
              let progressLabel = '';
              
              if (milestone.targetType === 'budget') {
                currentValue = stats.budget;
                progressPercent = Math.min(100, (currentValue / milestone.targetValue) * 100);
                progressLabel = `$${(currentValue / 1000).toFixed(0)}K / $${(milestone.targetValue / 1000).toFixed(0)}K`;
              } else if (milestone.targetType === 'compliance') {
                currentValue = stats.compliance;
                progressPercent = Math.min(100, (currentValue / milestone.targetValue) * 100);
                progressLabel = `${currentValue.toFixed(0)}% / ${milestone.targetValue}%`;
              } else if (milestone.targetType === 'product_count') {
                currentValue = stats.activeProducts.length;
                progressPercent = milestone.targetValue > 0 ? Math.min(100, (currentValue / milestone.targetValue) * 100) : 0;
                progressLabel = `${currentValue} / ${milestone.targetValue} products`;
              } else if (milestone.targetType === 'threat') {
                currentValue = stats.threat;
                progressPercent = milestone.targetValue === 0 ? (currentValue === 0 ? 100 : Math.max(0, 100 - currentValue)) : 0;
                progressLabel = `Threat: ${currentValue.toFixed(0)}%`;
              } else if (milestone.targetType === 'building' || milestone.targetType === 'tech') {
                progressLabel = milestone.completed ? 'Achieved' : 'In Progress';
                progressPercent = milestone.completed ? 100 : 0;
              }

              return (
                <div key={idx} className={`p-4 rounded-lg border transition-all ${
                  milestone.completed 
                    ? 'bg-emerald-950/20 border-emerald-700/50' 
                    : 'bg-white/5 border-white/10 hover:border-white/20'
                }`}>
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h5 className={`font-bold ${milestone.completed ? 'text-emerald-400' : 'text-white'}`}>
                          {milestone.name}
                        </h5>
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          milestone.targetType === 'budget' ? 'bg-emerald-900/50 text-emerald-300' :
                          milestone.targetType === 'compliance' ? 'bg-blue-900/50 text-blue-300' :
                          milestone.targetType === 'product_count' ? 'bg-orange-900/50 text-orange-300' :
                          milestone.targetType === 'threat' ? 'bg-red-900/50 text-red-300' :
                          'bg-purple-900/50 text-purple-300'
                        }`}>
                          {milestone.targetType.replace('_', ' ')}
                        </span>
                      </div>
                      <p className="text-sm text-slate-400 mt-1">{milestone.description}</p>
                    </div>
                    <div className="text-right ml-4">
                      <div className={`text-lg font-bold ${milestone.completed ? 'text-emerald-400' : 'text-yellow-400'}`}>
                        ${(milestone.reward / 1000).toFixed(0)}K
                      </div>
                      <div className="text-xs text-slate-500">reward</div>
                    </div>
                  </div>
                  
                  {/* Progress indicator */}
                  {!milestone.completed && (
                    <div className="mt-3">
                      <div className="flex justify-between text-xs text-slate-400 mb-1">
                        <span>{progressLabel}</span>
                        <span>{progressPercent.toFixed(0)}%</span>
                      </div>
                      <div className="w-full bg-slate-700 rounded-full h-1.5">
                        <div 
                          className={`h-1.5 rounded-full transition-all ${
                            progressPercent >= 100 ? 'bg-emerald-500' :
                            progressPercent >= 50 ? 'bg-cyan-500' :
                            'bg-slate-500'
                          }`}
                          style={{ width: `${progressPercent}%` }}
                        />
                      </div>
                    </div>
                  )}
                  
                  {milestone.completed && (
                    <div className="mt-2 flex items-center gap-2 text-emerald-400 text-sm">
                      <span>✓</span> Completed
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChecklistTab;
